import { useState, useRef, useEffect, useCallback, useMemo, isValidElement, type KeyboardEvent, type ReactNode } from 'react'
import {
  Monitor, Mic, ArrowUp, Square,
  Bot,
  Plus, Paperclip, ChevronRight,
  Plug, Zap, ToggleRight, ToggleLeft, Settings,
  Brain, Trash2, BarChart2, BookOpen, Lightbulb, X, FileText
} from 'lucide-react'

// ─── Slash-kommandoer (inspirert av Claude Code) ────────────────────────────────────
const SLASH_COMMANDS = [
  {
    command: '/husk',
    description: 'Lagre noe i minnet. Eks: /husk Jeg jobber med React',
    icon: 'Brain',
    example: '/husk [nøkkel]: [verdi]',
  },
  {
    command: '/glem',
    description: 'Slett et minne. Eks: /glem navn',
    icon: 'Trash2',
    example: '/glem [nøkkel]',
  },
  {
    command: '/plan',
    description: 'Aktiver planleggingsmodus – Sine lager en plan før den handler',
    icon: 'Lightbulb',
    example: '/plan [oppgave]',
  },
  {
    command: '/bruk',
    description: 'Vis token-forbruk for denne samtalen',
    icon: 'BarChart2',
    example: '/bruk',
  },
  {
    command: '/skills',
    description: 'List opp alle tilgjengelige skills',
    icon: 'BookOpen',
    example: '/skills',
  },
  {
    command: '/arbeid',
    aliases: ['/agent'],
    description: 'Start en dyp arbeidsflyt med verktøy, filer og Sine Computer',
    icon: 'Bot',
    example: '/arbeid [oppgave]',
  },
] as const

type SlashCommand = typeof SLASH_COMMANDS[number]

const SLASH_ICONS: Record<string, React.ReactNode> = {
  Brain: <Brain size={14} />,
  Trash2: <Trash2 size={14} />,
  Lightbulb: <Lightbulb size={14} />,
  BarChart2: <BarChart2 size={14} />,
  BookOpen: <BookOpen size={14} />,
  Bot: <Bot size={14} />,
}

function SlashCommandMenu({
  commands,
  selectedIndex,
  onSelect,
}: {
  commands: readonly SlashCommand[]
  selectedIndex: number
  onSelect: (cmd: SlashCommand) => void
}) {
  if (commands.length === 0) return null
  return (
    <div className="slash-menu">
      <div className="slash-menu-header">
        Kommandoer
      </div>
      {commands.map((cmd, i) => (
        <button
          key={cmd.command}
          onClick={() => onSelect(cmd)}
          className={`slash-menu-item${i === selectedIndex ? ' selected' : ''}`}
        >
          <span className="slash-menu-item-icon s-text-accent">{SLASH_ICONS[cmd.icon]}</span>
          <div className="slash-menu-item-body">
            <div className="slash-menu-item-row">
              <span className="slash-menu-item-cmd s-text-accent">{cmd.command}</span>
              <span className="slash-menu-item-desc s-text-secondary">{cmd.description}</span>
            </div>
          </div>
        </button>
      ))}
    </div>
  )
}
import type { ConnectorCapability, Skill } from '@/types'
import { getTranslations } from '@/i18n'
import { filterReadyConnectorIds, getConnectorCapability, isConnectorConnectable, isConnectorReady } from '@/shared/connectors/availability'
import type { AgentMode, AgentState } from '@/hooks/useAgent'
import { useTypewriterText } from '@/hooks/useTypewriterText'
import type { MessageAttachment, WorkspaceSurface } from '@/types'
import { AgentSettingsPopover, type AgentSettings } from './AgentSettingsPopover'
import type { UserMemory } from '@/types'
import { useApp } from '@/store/AppContext'
import { AgentTaskPanel } from '@/components/agent/AgentTaskPanel'
import { AgentStepCards } from '@/components/agent/AgentStepCards'
import { prepareMessageAttachments, type PendingMessageAttachment } from '@/lib/messageAttachments'

// ─── Suggestion pills data ────────────────────────────────────────────────────
const CHAT_SUGGESTIONS = [
  {
    label: 'Skriv',
    icon: '/skriv.svg',
    prompts: [
      {
        text: 'Skriv en profesjonell e-post',
        preview: 'Hei Sine! Kan du hjelpe meg med \u00e5 skrive en profesjonell e-post? Hvis du trenger mer informasjon fra meg, still meg 1-2 n\u00f8kkelsp\u00f8rsm\u00e5l med \u00e9n gang.',
      },
      {
        text: 'Lag et blogginnlegg',
        preview: 'Hei Sine! Kan du lage et engasjerende blogginnlegg for meg? Hvis du trenger mer informasjon om tema eller m\u00e5lgruppe, still meg 1-2 n\u00f8kkelsp\u00f8rsm\u00e5l med \u00e9n gang.',
      },
      {
        text: 'Skriv en produktbeskrivelse',
        preview: 'Hei Sine! Kan du skrive en overbevisende produktbeskrivelse? Hvis du trenger mer informasjon om produktet, still meg 1-2 n\u00f8kkelsp\u00f8rsm\u00e5l med \u00e9n gang.',
      },
      {
        text: 'Lag en presentasjon',
        preview: 'Hei Sine! Kan du hjelpe meg med \u00e5 lage en presentasjon? Hvis du trenger mer informasjon om tema eller publikum, still meg 1-2 n\u00f8kkelsp\u00f8rsm\u00e5l med \u00e9n gang.',
      },
      {
        text: 'Skriv en rapport',
        preview: 'Hei Sine! Kan du hjelpe meg med \u00e5 skrive en rapport? Hvis du trenger mer informasjon om innhold eller format, still meg 1-2 n\u00f8kkelsp\u00f8rsm\u00e5l med \u00e9n gang.',
      },
    ],
  },
  {
    label: 'L\u00e6r',
    icon: '/l\u00e6r.svg',
    prompts: [
      {
        text: 'Utforsk et fascinerende konsept',
        preview: 'Hei Sine! Kan du utforske et fascinerende konsept for meg? Hvis du trenger mer informasjon fra meg, still meg 1-2 n\u00f8kkelsp\u00f8rsm\u00e5l med \u00e9n gang.',
      },
      {
        text: 'Forklar maskinl\u00e6ring enkelt',
        preview: 'Hei Sine! Kan du forklare maskinl\u00e6ring p\u00e5 en enkel og forst\u00e5elig m\u00e5te? Bruk gjerne eksempler fra hverdagen.',
      },
      {
        text: 'Hva er forskjellen mellom...',
        preview: 'Hei Sine! Kan du forklare forskjellen mellom to konsepter for meg? Hvis du trenger mer informasjon om hva jeg vil sammenligne, still meg 1-2 n\u00f8kkelsp\u00f8rsm\u00e5l.',
      },
      {
        text: 'Gi meg en oversikt over et tema',
        preview: 'Hei Sine! Kan du gi meg en god oversikt over et tema jeg er nysgjerrig p\u00e5? Hvis du trenger mer informasjon om hva jeg vil l\u00e6re, still meg 1-2 n\u00f8kkelsp\u00f8rsm\u00e5l.',
      },
      {
        text: 'L\u00e6r meg grunnleggende Python',
        preview: 'Hei Sine! Kan du l\u00e6re meg grunnleggende Python-programmering? Start med de viktigste konseptene og gi meg praktiske eksempler underveis.',
      },
    ],
  },
  {
    label: 'Kode',
    icon: '/kode.svg',
    prompts: [
      {
        text: 'Skriv en React-komponent',
        preview: 'Hei Sine! Kan du skrive en React-komponent for meg? Hvis du trenger mer informasjon om funksjonalitet eller design, still meg 1-2 n\u00f8kkelsp\u00f8rsm\u00e5l med \u00e9n gang.',
      },
      {
        text: 'Fiks en bug i koden min',
        preview: 'Hei Sine! Kan du hjelpe meg med \u00e5 finne og fikse en bug i koden min? Lim inn koden din nedenfor, s\u00e5 ser jeg p\u00e5 det.',
      },
      {
        text: 'Optimaliser denne funksjonen',
        preview: 'Hei Sine! Kan du optimalisere en funksjon for bedre ytelse? Lim inn koden din nedenfor, s\u00e5 foresl\u00e5r jeg forbedringer.',
      },
      {
        text: 'Lag en REST API',
        preview: 'Hei Sine! Kan du hjelpe meg med \u00e5 lage en REST API? Hvis du trenger mer informasjon om endepunkter eller teknologi, still meg 1-2 n\u00f8kkelsp\u00f8rsm\u00e5l med \u00e9n gang.',
      },
      {
        text: 'Forklar denne koden',
        preview: 'Hei Sine! Kan du forklare hva denne koden gj\u00f8r? Lim inn koden din nedenfor, s\u00e5 gir jeg deg en tydelig forklaring.',
      },
    ],
  },
  {
    label: 'Livet',
    icon: '/livet.svg',
    prompts: [
      {
        text: 'Hjelp meg med \u00e5 ta en beslutning',
        preview: 'Hei Sine! Kan du hjelpe meg med \u00e5 ta en vanskelig beslutning? Hvis du trenger mer informasjon om situasjonen, still meg 1-2 n\u00f8kkelsp\u00f8rsm\u00e5l med \u00e9n gang.',
      },
      {
        text: 'Lag en treningsplan',
        preview: 'Hei Sine! Kan du lage en personlig treningsplan for meg? Hvis du trenger mer informasjon om m\u00e5l eller niv\u00e5, still meg 1-2 n\u00f8kkelsp\u00f8rsm\u00e5l med \u00e9n gang.',
      },
      {
        text: 'Planlegg en reise',
        preview: 'Hei Sine! Kan du hjelpe meg med \u00e5 planlegge en reise? Hvis du trenger mer informasjon om destinasjon eller budsjett, still meg 1-2 n\u00f8kkelsp\u00f8rsm\u00e5l med \u00e9n gang.',
      },
      {
        text: 'Gi meg oppskrifter til middag',
        preview: 'Hei Sine! Kan du foresl\u00e5 noen gode middagsoppskrifter? Hvis du trenger mer informasjon om preferanser eller ingredienser, still meg 1-2 n\u00f8kkelsp\u00f8rsm\u00e5l med \u00e9n gang.',
      },
      {
        text: 'Hjelp meg med \u00e5 organisere hverdagen',
        preview: 'Hei Sine! Kan du hjelpe meg med \u00e5 organisere hverdagen bedre? Hvis du trenger mer informasjon om rutiner eller utfordringer, still meg 1-2 n\u00f8kkelsp\u00f8rsm\u00e5l med \u00e9n gang.',
      },
    ],
  },
  {
    label: 'Sines valg',
    icon: '/sinesvalg.svg',
    prompts: [
      {
        text: 'Overrask meg med noe interessant',
        preview: 'Hei Sine! Overrask meg med noe interessant - et fascinerende faktum, en tankevekkende id\u00e9 eller en kreativ utfordring. Velg noe du synes er spennende.',
      },
      {
        text: 'Gi meg en kreativ utfordring',
        preview: 'Hei Sine! Gi meg en kreativ utfordring jeg kan jobbe med i dag. Velg noe som er morsomt og inspirerende.',
      },
      {
        text: 'Fortell meg noe jeg ikke visste',
        preview: 'Hei Sine! Fortell meg noe overraskende eller lite kjent som du synes er fascinerende. Velg et tema du brenner for.',
      },
      {
        text: 'Hva b\u00f8r jeg l\u00e6re i dag?',
        preview: 'Hei Sine! Hva mener du jeg b\u00f8r l\u00e6re eller utforske i dag? Velg noe du synes er verdifullt og interessant for meg.',
      },
      {
        text: 'Inspirer meg',
        preview: 'Hei Sine! Inspirer meg med noe - et sitat, en id\u00e9, en historie eller en utfordring. Velg noe du synes er genuint inspirerende.',
      },
    ],
  },
]

const AGENT_SUGGESTIONS = [
  {
    label: 'Automatiser',
    prompts: [
      { text: 'Send en e-post til alle kunder', preview: 'Send en e-post til alle kunder om...' },
      { text: 'Oppdater Meta Ads-budsjettet', preview: 'Oppdater Meta Ads-budsjettet for kampanjen...' },
      { text: 'Post p\u00e5 Instagram', preview: 'Post dette innholdet p\u00e5 Instagram med hashtags...' },
      { text: 'Synkroniser kalenderen min', preview: 'Synkroniser kalenderen min med...' },
    ],
  },
  {
    label: 'Rapporter',
    prompts: [
      { text: 'Lag en ukentlig salgsrapport', preview: 'Lag en ukentlig salgsrapport basert p\u00e5 data fra...' },
      { text: 'Analyser nettstedstrafikk', preview: 'Analyser nettstedstrafikken og lag en rapport...' },
      { text: 'Oppsummer alle e-poster fra i dag', preview: 'Oppsummer alle e-poster fra i dag og prioriter dem...' },
      { text: 'Generer en GitHub-aktivitetsrapport', preview: 'Generer en rapport over GitHub-aktivitet for...' },
    ],
  },
  {
    label: 'S\u00f8k',
    prompts: [
      { text: 'Finn informasjon om', preview: 'Finn informasjon om temaet og oppsummer det...' },
      { text: 'S\u00f8k etter konkurrenter til', preview: 'S\u00f8k etter konkurrenter til produktet mitt og analyser dem...' },
      { text: 'Finn de beste verkt\u00f8yene for', preview: 'Finn de beste verkt\u00f8yene for oppgaven...' },
      { text: 'Research markedet for', preview: 'Research markedet for produktet eller tjenesten...' },
    ],
  },
  {
    label: 'Organiser',
    prompts: [
      { text: 'Organiser innboksen min', preview: 'Organiser innboksen min og lag mapper for...' },
      { text: 'Planlegg uken min', preview: 'Planlegg uken min basert p\u00e5 oppgavene mine...' },
      { text: 'Lag en prosjektplan for', preview: 'Lag en detaljert prosjektplan for...' },
      { text: 'Rydd opp i GitHub-repoet', preview: 'Rydd opp i GitHub-repoet mitt ved \u00e5...' },
    ],
  },
]

function SuggestionPillIcon({ src, label }: { src: string; label: string }) {
  const [failed, setFailed] = useState(false)
  if (failed) return null
  return (
    <img
      src={src}
      alt={label}
      width={16}
      height={16}
      onError={() => setFailed(true)}
       className="connector-icon-img"
    />
  )
}

function renderSuggestionIcon(icon: unknown, label: string): ReactNode {
  if (!icon) return null
  if (typeof icon === 'string') {
    const isImagePath = icon.startsWith('/') || icon.startsWith('http://') || icon.startsWith('https://')
    if (isImagePath) {
      return <SuggestionPillIcon src={icon} label={label} />
    }
    return <span className="suggestion-icon-img">{icon}</span>
  }
  if (isValidElement(icon)) {
    return <span className="suggestion-icon-emoji">{icon}</span>
  }
  return null
}

function normalizeSuggestionPrompts(
  suggestion: { label: string; prompts?: Array<{ text: string; preview: string }> }
) {
  if (Array.isArray(suggestion.prompts) && suggestion.prompts.length > 0) {
    return suggestion.prompts
  }
  return [{ text: suggestion.label, preview: suggestion.label }]
}

function SuggestionPills({
  useAgentMode,
  suggestions: suggestionsProp,
  onSelect,
  onHoverPreview,
  onHoverEnd,
  animationSeed,
}: {
  useAgentMode: boolean
  suggestions?: typeof CHAT_SUGGESTIONS | typeof AGENT_SUGGESTIONS
  onSelect: (text: string) => void
  onHoverPreview: (text: string) => void
  onHoverEnd: () => void
  animationSeed?: string
}) {
  const suggestions = (suggestionsProp as typeof CHAT_SUGGESTIONS) ?? (useAgentMode ? AGENT_SUGGESTIONS : CHAT_SUGGESTIONS)
  const [openPill, setOpenPill] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        containerRef.current && !containerRef.current.contains(e.target as Node)
      ) {
        setOpenPill(null)
        onHoverEnd()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onHoverEnd])

  const openSug = suggestions.find(s => s.label === openPill)
  const openSugPrompts = openSug
    ? normalizeSuggestionPrompts(openSug as { label: string; prompts?: Array<{ text: string; preview: string }> })
    : []

  return (
    <div className="suggestion-pills-wrapper">
      {/* Pill row */}
      <div
        ref={containerRef}
        className="suggestion-pills-row"
      >
        {suggestions.map((sug, index) => (
          <button
            key={`${animationSeed ?? 'default'}-${sug.label}`}
            className={`suggestion-pill suggestion-pill--intro${openPill === sug.label ? ' active' : ''}`}
            style={{ animationDelay: `${index * 80}ms` }}
            onClick={() => {
              if (openPill === sug.label) {
                setOpenPill(null)
                onHoverEnd()
              } else {
                setOpenPill(sug.label)
              }
            }}
          >
            {'icon' in sug && renderSuggestionIcon(sug.icon, sug.label)}
            <span>{sug.label}</span>
          </button>
        ))}
      </div>

      {/* Dropdown panel — positioned below the pill row */}
      {openPill && openSug && (
        <div
          ref={dropdownRef}
          className="suggestion-dropdown-panel"
        >
          {/* Header */}
          <div className="suggestion-dropdown-header">
            <div className="suggestion-dropdown-header-left">
              {'icon' in openSug && renderSuggestionIcon(openSug.icon, openSug.label)}
              <span>{openSug.label}</span>
            </div>
            <button
              className="suggestion-dropdown-close"
              onClick={() => { setOpenPill(null); onHoverEnd() }}
              aria-label="Lukk"
            >
              <X size={14} />
            </button>
          </div>

          {/* Items */}
          <div className="suggestion-dropdown-items">
            {openSugPrompts.map((p, i) => (
              <button
                key={i}
                className="suggestion-dropdown-item"
                onMouseEnter={() => onHoverPreview(p.preview)}
                onMouseLeave={onHoverEnd}
                onClick={() => {
                  onSelect(p.preview)
                  setOpenPill(null)
                  onHoverEnd()
                }}
              >
                {p.text}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface ChatInputProps {
  onSend: (message: string, mode?: AgentMode, attachments?: MessageAttachment[], backendMessage?: string) => void
  onStop?: () => void
  isStreaming?: boolean
  language: 'no' | 'en'
  disabled?: boolean
  agentMode?: AgentMode
  onAgentModeChange?: (mode: AgentMode) => void
  isAgentActive?: boolean
  useAgentMode?: boolean
  runtimeSurface?: WorkspaceSurface
  showAgentControls?: boolean
  onToggleAgentMode?: () => void
  compact?: boolean
  /** When true the input is in the centered welcome layout — connector popup opens downward */
  isWelcome?: boolean
  agentState?: AgentState
  onOpenTerminal?: () => void
  isTerminalOpen?: boolean
  agentSettings?: AgentSettings
  onAgentSettingsChange?: (s: AgentSettings) => void
  memory?: UserMemory[]
  onAddMemory?: (key: string, value: string) => void
  onRemoveMemory?: (id: string) => void
  onClearMemory?: () => void
  /** Whether the conversation already has messages (hides suggestion pills when true) */
  hasMessages?: boolean
  /** Per-chat enabled connector IDs (null = not set, use all connected) */
  enabledConnectors?: string[] | null
  /** Callback to toggle a connector for this chat */
  onToggleConnector?: (connectorId: string, enabled: boolean) => void
  /** Override suggestion pills (e.g. for Studio mode) */
  suggestionOverride?: typeof CHAT_SUGGESTIONS | typeof AGENT_SUGGESTIONS
  animatedPlaceholderText?: string
  welcomeAnimationKey?: string
}

// ─── Skills dropdown i chat ───────────────────────────────────
function SkillsDropdown({
  skills,
  onToggle,
  onClose,
  onOpenSettings,
}: {
  skills: Skill[]
  onToggle: (id: string) => void
  onClose: () => void
  onOpenSettings: () => void
}) {
  return (
    <div className="chat-skills-dropdown">
      <div className="chat-skills-header">
        <span>Aktive Skills</span>
        <button
          className="chat-skills-manage"
          onClick={() => { onOpenSettings(); onClose() }}
        >
          Administrer
        </button>
      </div>
      {skills.length === 0 ? (
        <div className="chat-skills-empty">
          <p>Ingen skills lagt til ennå.</p>
          <button
            className="chat-skills-add-link"
            onClick={() => { onOpenSettings(); onClose() }}
          >
            Legg til skills →
          </button>
        </div>
      ) : (
        <div className="chat-skills-list">
          {skills.map(skill => (
            <button
              key={skill.id}
              className={`chat-skill-item${skill.enabled ? ' active' : ''}`}
              onClick={() => onToggle(skill.id)}
            >
              <span className="chat-skill-icon">
                {skill.icon ? <span className="skill-icon-emoji">{skill.icon}</span> : <Zap size={13} />}
              </span>
              <span className="chat-skill-name">{skill.name}</span>
              {skill.enabled
                ? <ToggleRight size={16} className="skill-toggle-on" />
                : <ToggleLeft size={16} className="skill-toggle-off" />
              }
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Connectors popup i chat — matches reference design ──────
const APP_CONNECTORS_MINI = [
  { id: 'gmail', name: 'Gmail', hasSub: false },
  { id: 'github', name: 'GitHub', hasSub: true },
  { id: 'chrome', name: 'Google Chrome Extension', hasSub: false },
  { id: 'mcp', name: 'Custom MCP', hasSub: false },
]

// Emoji fallbacks for connectors (used if SVG not found)
const CONNECTOR_EMOJI: Record<string, string> = {
  gmail: '✉️',
  github: '🐙',
  chrome: '🖥️',
  mcp: '⚡',
}

function ConnectorIcon({ id, size = 16 }: { id: string; size?: number }) {
  const [failed, setFailed] = useState(false)
  const src = `/connector-icons/${id}.svg`
  if (failed) {
    return <span className="connector-icon-emoji">{CONNECTOR_EMOJI[id] ?? '🔌'}</span>
  }
  return (
    <img
      src={src}
      alt={id}
      width={size}
      height={size}
      onError={() => setFailed(true)}
      className="connector-icon-img"
    />
  )
}

function ConnectorsPopup({
  statuses,
  capabilities,
  disabled: disabledMap,
  onClose,
  onOpenSettings,
  connectLabel,
  manageLabel,
  enableLabel,
  disableLabel,
  onConnect,
  isWelcome = false,
  enabledConnectors = null,
  onToggleConnector,
  onToggleDisabled,
  openDownward = false,
}: {
  statuses: Record<string, string>
  capabilities: Record<string, ConnectorCapability>
  disabled: Record<string, boolean>
  onClose: () => void
  onOpenSettings: () => void
  connectLabel: string
  manageLabel: string
  enableLabel: string
  disableLabel: string
  onConnect?: (id: string) => void
  isWelcome?: boolean
  enabledConnectors?: string[] | null
  onToggleConnector?: (id: string, enabled: boolean) => void
  onToggleDisabled?: (id: string) => void
  openDownward?: boolean
}) {
  return (
    <div className={`connectors-mini-popup${openDownward ? ' open-downward' : ''}`} style={isWelcome && !openDownward ? { bottom: 'auto', top: 'calc(100% + 8px)' } : undefined}>
      {APP_CONNECTORS_MINI.map(c => {
        const capability = getConnectorCapability(c.id, capabilities)
        const isConnected = isConnectorReady(capability, statuses[c.id])
        const isImplemented = capability.implemented
        const isConfigured = capability.configured
        const isConnectable = isConnectorConnectable(capability)
        const isChatScoped = enabledConnectors !== null && Boolean(onToggleConnector)
        const isChatEnabled = isConnected && (enabledConnectors ? enabledConnectors.includes(c.id) : true)
        const isGloballyEnabled = isConnected && !disabledMap[c.id]
        const toggleChecked = isChatScoped ? isChatEnabled : isGloballyEnabled
        const toggleTitle = isChatScoped
          ? (toggleChecked ? 'Slå av i denne chatten' : 'Slå på i denne chatten')
          : (toggleChecked ? disableLabel : enableLabel)
        return (
          <div key={c.id} className="connectors-mini-row">
            <div className="connectors-mini-icon">
              <ConnectorIcon id={c.id} size={16} />
            </div>
            <span className="connectors-mini-name">
              {c.name}
              {c.beta && <span className="connectors-mini-beta">Beta</span>}
              {!isImplemented && <span className="connectors-mini-beta">Soon</span>}
              {isImplemented && !isConfigured && <span className="connectors-mini-beta">Setup</span>}
              {isConnected && isChatScoped && !isChatEnabled && <span className="connectors-mini-beta">Av i chat</span>}
            </span>
            {isConnected ? (
              <div className="connectors-mini-right">
                {c.id === 'github' && c.hasSub && (
                  <button className="connectors-mini-sub">
                    Repositories <ChevronRight size={11} />
                  </button>
                )}
                <label className="connectors-mini-toggle" title={toggleTitle}>
                  <input
                    type="checkbox"
                    checked={toggleChecked}
                    onChange={() => {
                      if (isChatScoped) {
                        onToggleConnector?.(c.id, !toggleChecked)
                        return
                      }
                      onToggleDisabled?.(c.id)
                    }}
                    style={{ display: 'none' }}
                  />
                  <span className={`connectors-mini-toggle-track${toggleChecked ? ' on' : ''}`} />
                </label>
              </div>
            ) : (
              <button
                className="connectors-mini-connect"
                disabled={!isConnectable}
                title={
                  !isImplemented
                    ? 'Not implemented yet'
                    : !isConfigured
                      ? 'Backend setup missing'
                      : undefined
                }
                style={!isConnectable ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                onClick={() => {
                  if (onConnect) {
                    onConnect(c.id)
                    onClose()
                  } else {
                    onOpenSettings()
                    onClose()
                  }
                }}
              >
                {!isImplemented ? 'Soon' : !isConfigured ? 'Setup' : connectLabel}
              </button>
            )}
          </div>
        )
      })}
      <button
        className="connectors-mini-manage"
        onClick={() => { onOpenSettings(); onClose() }}
      >
        <Settings size={12} />
        <span>{manageLabel}</span>
      </button>
    </div>
  )
}

export function ChatInput({
  onSend,
  onStop,
  isStreaming,
  language,
  disabled,
  agentMode = 'safe',
  onAgentModeChange,
  isAgentActive,
  useAgentMode = false,
  runtimeSurface = useAgentMode ? 'agent' : 'chat',
  showAgentControls = useAgentMode,
  onToggleAgentMode,
  compact = false,
  isWelcome = false,
  agentState,
  onOpenTerminal,
  isTerminalOpen = false,
  agentSettings,
  onAgentSettingsChange,
  memory = [],
  onAddMemory,
  onRemoveMemory,
  onClearMemory,
  hasMessages = false,
  enabledConnectors = null,
  onToggleConnector,
  suggestionOverride,
  animatedPlaceholderText,
  welcomeAnimationKey,
}: ChatInputProps) {
  const [value, setValue] = useState('')
  const [ghostText, setGhostText] = useState('')
  const [showPlusMenu, setShowPlusMenu] = useState(false)
  const [showSkillsDropdown, setShowSkillsDropdown] = useState(false)
  const [showConnectorsPopup, setShowConnectorsPopup] = useState(false)
  const [slashMenuOpen, setSlashMenuOpen] = useState(false)
  const [slashSelectedIndex, setSlashSelectedIndex] = useState(0)
  const [planningMode, setPlanningMode] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const plusMenuRef = useRef<HTMLDivElement>(null)
  const skillsRef = useRef<HTMLDivElement>(null)
  const connectorsRef = useRef<HTMLDivElement>(null)
  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const t = getTranslations(language)

  const { settings, updateSettings, setSettingsOpen } = useApp()
  const skills: Skill[] = settings.skills ?? []
  const connectorStatuses = settings.connectorStatuses ?? {}
  const connectorCapabilities = settings.connectorCapabilities ?? {}
  const connectorDisabled = settings.connectorDisabled ?? {}
  // Count connectors: if per-chat enabledConnectors is set, use that; else count connected & not globally disabled
  const visibleConnectorIds = new Set(APP_CONNECTORS_MINI.map(c => c.id))
  const allConnectedIds = filterReadyConnectorIds(
    Object.keys(connectorCapabilities).filter(id => visibleConnectorIds.has(id)),
    connectorCapabilities,
    connectorStatuses,
    connectorDisabled,
  )
  const connectedCount = enabledConnectors !== null
    ? enabledConnectors.filter(id => {
      const capability = connectorCapabilities[id]
      const isConnected = isConnectorReady(capability, connectorStatuses[id])
      return visibleConnectorIds.has(id) && isConnected && !connectorDisabled[id]
    }).length
    : allConnectedIds.length
  const enabledSkillsCount = skills.filter(s => s.enabled).length

  const effectiveAgentSettings: AgentSettings = agentSettings ?? {
    agentType: 'code',
    agentMode: agentMode,
    outputLanguage: 'auto',
    detailLevel: 'normal',
    documentType: 'report',
    autoSave: false,
  }

  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (plusMenuRef.current && !plusMenuRef.current.contains(e.target as Node)) {
        setShowPlusMenu(false)
      }
      if (skillsRef.current && !skillsRef.current.contains(e.target as Node)) {
        setShowSkillsDropdown(false)
      }
      if (connectorsRef.current && !connectorsRef.current.contains(e.target as Node)) {
        setShowConnectorsPopup(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Voice input handler
  const handleVoiceInput = useCallback(() => {
    const SpeechRecognitionAPI = (window as unknown as { SpeechRecognition?: typeof SpeechRecognition; webkitSpeechRecognition?: typeof SpeechRecognition }).SpeechRecognition
      ?? (window as unknown as { SpeechRecognition?: typeof SpeechRecognition; webkitSpeechRecognition?: typeof SpeechRecognition }).webkitSpeechRecognition
    if (!SpeechRecognitionAPI) {
      alert('Talegjenkjenning støttes ikke i denne nettleseren. Prøv Chrome eller Edge.')
      return
    }
    if (isRecording) {
      recognitionRef.current?.stop()
      setIsRecording(false)
      return
    }
    const recognition = new SpeechRecognitionAPI()
    recognition.lang = language === 'en' ? 'en-US' : 'nb-NO'
    recognition.interimResults = true
    recognition.maxAlternatives = 1
    recognitionRef.current = recognition
    let finalTranscript = ''
    recognition.onresult = (event) => {
      let interim = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript
        if (event.results[i].isFinal) finalTranscript += t
        else interim += t
      }
      setValue(prev => {
        const base = prev.replace(/\[taler…\].*$/, '').trimEnd()
        if (interim) return base + (base ? ' ' : '') + '[taler…] ' + interim
        return base + (base && finalTranscript ? ' ' : '') + finalTranscript
      })
    }
    recognition.onend = () => {
      setIsRecording(false)
      setValue(prev => prev.replace(/\[taler…\].*$/, '').trimEnd())
      setTimeout(() => textareaRef.current?.focus(), 100)
    }
    recognition.onerror = () => {
      setIsRecording(false)
    }
    recognition.start()
    setIsRecording(true)
  }, [isRecording, language])

  // Listen for prefill-chat event (from "Build with Sine")
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        prompt?: string
        attachments?: PendingMessageAttachment[]
      }
      if (detail?.prompt) {
        setValue(detail.prompt)
        setTimeout(() => textareaRef.current?.focus(), 100)
      }
      if (Array.isArray(detail?.attachments) && detail.attachments.length > 0) {
        setAttachments(prev => [...prev, ...detail.attachments])
      }
    }
    window.addEventListener('sine:prefill-chat', handler)
    return () => window.removeEventListener('sine:prefill-chat', handler)
  }, [])

  // 3 lines minimum: font-size 14px * line-height 1.6 = 22.4px/line
  // + padding-top 16px + padding-bottom 8px = 92px
  const MIN_TEXTAREA_HEIGHT = 92
  const MAX_TEXTAREA_HEIGHT = 320

  const adjustHeight = useCallback(() => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = 'auto'
    const newHeight = Math.max(MIN_TEXTAREA_HEIGHT, Math.min(ta.scrollHeight, MAX_TEXTAREA_HEIGHT))
    ta.style.height = newHeight + 'px'
  }, [])

  // ── Slash-kommando: filtrer basert på input ───────────────────────────────────
  const filteredSlashCommands = useMemo(() => (
    value.startsWith('/')
      ? SLASH_COMMANDS.filter(c => c.command.startsWith(value.split(' ')[0].toLowerCase()))
      : []
  ), [value])

  // ── Slash-kommando: håndter utførelse ─────────────────────────────────────────
  const executeSlashCommand = useCallback((cmd: SlashCommand, fullInput: string) => {
    const args = fullInput.slice(cmd.command.length).trim()

    switch (cmd.command) {
      case '/husk': {
        if (args) {
          const colonIdx = args.indexOf(':')
          if (colonIdx > 0) {
            const key = args.slice(0, colonIdx).trim()
            const val = args.slice(colonIdx + 1).trim()
            onAddMemory?.(key, val)
            onSend(`Jeg har lagret i minnet: **${key}** = ${val}`, agentMode)
          } else {
            onAddMemory?.('notat', args)
            onSend(`Jeg har lagret i minnet: ${args}`, agentMode)
          }
        } else {
          setValue('/husk ')
          textareaRef.current?.focus()
          return
        }
        break
      }
      case '/glem': {
        if (args) {
          const toRemove = memory.find(m => m.key.toLowerCase() === args.toLowerCase())
          if (toRemove) {
            onRemoveMemory?.(toRemove.id)
            onSend(`Jeg har slettet minnet: **${args}**`, agentMode)
          } else {
            onSend(`Fant ikke minnet "${args}". Tilgjengelige minner: ${memory.map(m => m.key).join(', ') || 'ingen'}`, agentMode)
          }
        } else {
          onClearMemory?.()
          onSend('Jeg har slettet alle lagrede minner.', agentMode)
        }
        break
      }
      case '/plan': {
        setPlanningMode(true)
        const task = args || 'Beskriv oppgaven'
        onSend(`/plan ${task}`, agentMode)
        break
      }
      case '/bruk': {
        const memCount = memory.length
        onSend(
          `**Minnestatus:** ${memCount} minner lagret.\n` +
          (memCount > 0
            ? `\n**Lagrede minner:**\n${memory.map(m => `- **${m.key}**: ${m.value}`).join('\n')}`
            : ''),
          agentMode
        )
        break
      }
      case '/skills': {
        const activeSkills = (settings.skills ?? []).filter(s => s.enabled)
        const allSkills = settings.skills ?? []
        onSend(
          `**Skills (${activeSkills.length}/${allSkills.length} aktive):**\n` +
          (allSkills.length > 0
            ? allSkills.map(s => `- ${s.enabled ? '✅' : '□'} **${s.name}**: ${s.description}`).join('\n')
            : t.chat.noSkillsAdded),
          agentMode
        )
        break
      }
      case '/arbeid': {
        // Start deep-work controls without presenting a separate product mode.
        if (!showAgentControls) {
          onToggleAgentMode?.()
        }
        if (args) {
          // Small delay to let deep-work controls propagate before sending.
          setTimeout(() => {
            onSend(args, 'safe')
          }, 50)
        } else {
          // No task given — reveal deep-work controls and focus input.
          setValue('')
          textareaRef.current?.focus()
          return
        }
        break
      }
    }

    setValue('')
    setSlashMenuOpen(false)
    if (textareaRef.current) textareaRef.current.style.height = 'auto'
  }, [memory, onAddMemory, onRemoveMemory, onClearMemory, onSend, agentMode, settings.skills, showAgentControls, onToggleAgentMode, t.chat.noSkillsAdded])

  const isBusy = showAgentControls ? Boolean(isAgentActive) : Boolean(isStreaming)

  const [attachments, setAttachments] = useState<PendingMessageAttachment[]>([])

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    const newAttachments = Array.from(files).map(f => ({
      file: f,
      name: f.name,
      type: f.type,
      url: URL.createObjectURL(f),
      size: f.size,
    }))
    setAttachments(prev => [...prev, ...newAttachments])
    setShowPlusMenu(false)
    textareaRef.current?.focus()
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const clearAttachments = useCallback(() => {
    setAttachments(prev => {
      prev.forEach(att => URL.revokeObjectURL(att.url))
      return []
    })
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [])

  const handleSend = useCallback(async () => {
    const trimmed = value.trim()
    if ((!trimmed && attachments.length === 0) || isBusy || disabled) return

    if (trimmed.startsWith('/')) {
      const matchedCmd = SLASH_COMMANDS.find(c => (
        trimmed.startsWith(c.command)
        || ('aliases' in c && c.aliases.some(alias => trimmed.startsWith(alias)))
      ))
      if (matchedCmd) {
        const alias = 'aliases' in matchedCmd
          ? matchedCmd.aliases.find(item => trimmed.startsWith(item))
          : undefined
        const normalizedSlashInput = alias
          ? `${matchedCmd.command}${trimmed.slice(alias.length)}`
          : trimmed
        executeSlashCommand(matchedCmd, normalizedSlashInput)
        return
      }
    }

    const preparedAttachments = attachments.length > 0
      ? await prepareMessageAttachments(attachments)
      : { metadata: [], promptSuffix: '' }
    const userText = trimmed || 'Se vedlagte filer.'
    const backendText = preparedAttachments.promptSuffix
      ? [trimmed, preparedAttachments.promptSuffix].filter(Boolean).join('\n')
      : userText

    // Store raw File objects so the agent hook can upload binaries to the workspace
    const fileMap = new Map<string, File>(attachments.map(a => [a.name, a.file]))
    ;(window as Window & { __pendingAttachmentFiles?: Map<string, File> }).__pendingAttachmentFiles = fileMap

    if (planningMode) {
      onSend(userText, agentMode, preparedAttachments.metadata, `[PLANLEGGINGSMODUS] ${backendText}`)
    } else {
      onSend(userText, agentMode, preparedAttachments.metadata, backendText)
    }
    setValue('')
    setGhostText('')
    clearAttachments()
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }, [value, isBusy, disabled, attachments, onSend, agentMode, planningMode, executeSlashCommand, clearAttachments])

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (slashMenuOpen && filteredSlashCommands.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSlashSelectedIndex(i => (i + 1) % filteredSlashCommands.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSlashSelectedIndex(i => (i - 1 + filteredSlashCommands.length) % filteredSlashCommands.length)
        return
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
        e.preventDefault()
        const cmd = filteredSlashCommands[slashSelectedIndex]
        if (cmd) {
          setValue(cmd.command + ' ')
          setSlashMenuOpen(false)
          textareaRef.current?.focus()
        }
        return
      }
      if (e.key === 'Escape') {
        setSlashMenuOpen(false)
        return
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleSend()
    }
  }, [handleSend, slashMenuOpen, filteredSlashCommands, slashSelectedIndex])

  const removeAttachment = (idx: number) => {
    setAttachments(prev => {
      const next = [...prev]
      URL.revokeObjectURL(next[idx].url)
      next.splice(idx, 1)
      return next
    })
  }

  const handleSkillToggle = (id: string) => {
    const updated = skills.map(s => s.id === id ? { ...s, enabled: !s.enabled } : s)
    updateSettings({ skills: updated })
  }

  const canSend = (value.trim().length > 0 || attachments.length > 0) && !disabled

  const placeholder = ghostText
    ? ''
    : isAgentActive
      ? t.chat.giveAgentNewTask
      : showAgentControls
        ? (effectiveAgentSettings.agentType === 'writing'
          ? t.chat.describeWritingTask
          : t.chat.describeAgentTask)
        : t.app.placeholder
  const animatedPlaceholder = useTypewriterText(animatedPlaceholderText ?? placeholder, {
    active: Boolean(animatedPlaceholderText) && isWelcome && !value && !ghostText,
    resetKey: `${welcomeAnimationKey ?? 'welcome'}-${animatedPlaceholderText ?? placeholder}`,
    speedMs: 12,
    startDelayMs: 220,
  })
  const hasVisibleAgentProgress = Boolean(
    agentState && (
      (agentState.planningTasks?.length ?? 0) > 0 ||
      (agentState.liveTasks?.length ?? 0) > 0 ||
      agentState.browserSession ||
      agentState.browserFrame ||
      (agentState.terminalFrames?.length ?? 0) > 0 ||
      (agentState.files?.length ?? 0) > 0 ||
      (agentState.liveFiles?.length ?? 0) > 0 ||
      Boolean(agentState.pendingApproval)
    )
  )
  // Show step cards after first message (not just when active), hide when side panel is open
  const showLiveTask = hasVisibleAgentProgress && hasMessages && !isTerminalOpen
  const lastTask = agentState?.liveTasks?.slice(-1)[0]
  const currentTaskLabel = lastTask?.label ?? agentState?.currentTask ?? t.chat.planning

  return (
    <div
      className="chat-input-area"
      style={compact ? { padding: 0, margin: 0 } : undefined}
    >
      {/* ── Agent Step Cards (Manus-style collapsible steps with action chips) ── */}
      {showLiveTask && !compact && agentState && (agentState.planningTasks?.length ?? 0) > 0 && (
          <AgentStepCards
            steps={agentState.planningTasks ?? []}
            isActive={['planning', 'running', 'waiting_approval', 'needs_takeover', 'takeover_active'].includes(agentState.status)}
            metrics={agentState.metrics}
            logs={agentState.logs ?? []}
            agentState={agentState}
            onOpenPanel={onOpenTerminal}
            isPanelOpen={isTerminalOpen}
          />
      )}
      {showLiveTask && !compact && agentState && (agentState.planningTasks?.length ?? 0) === 0 && (
        <AgentTaskPanel
          planningTasks={[]}
          currentActionLabel={currentTaskLabel}
          startedAt={agentState.startedAt}
          status={agentState.status}
          currentTask={currentTaskLabel}
        />
      )}

      {/* ── Hoved input-boks (med vedlegg inni) ── */}
      <div className={`chat-input-box${showAgentControls ? ' agent-mode' : ''} chat-input-box-rel`} data-runtime-surface={runtimeSurface}>
        {/* Slash-kommando meny */}
        {slashMenuOpen && filteredSlashCommands.length > 0 && (
          <SlashCommandMenu
            commands={filteredSlashCommands}
            selectedIndex={slashSelectedIndex}
            onSelect={(cmd) => {
              setValue(cmd.command + ' ')
              setSlashMenuOpen(false)
              textareaRef.current?.focus()
            }}
          />
        )}

        {/* ── Attachment previews (inside the input box) ── */}
        {attachments.length > 0 && (
          <div className="chat-attachments-row">
            {attachments.map((att, idx) => (
              <div key={idx} className="chat-attachment-thumb">
                {att.type.startsWith('image/') ? (
                  <img src={att.url} alt={att.name} className="chat-attachment-img" />
                ) : (
                  <div className="chat-attachment-file">
                    <FileText size={24} className="s-text-muted" />
                    <span className="chat-attachment-filename s-text-secondary">{att.name}</span>
                  </div>
                )}
                <button
                  onClick={() => removeAttachment(idx)}
                  className="chat-attachment-remove"
                  title={t.chat.removeAttachment}
                >
                  <X size={10} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Ghost text overlay */}
        {ghostText && !value && (
          <div className={`chat-ghost-text${attachments.length > 0 ? ' chat-ghost-text--below-attachments' : ''}`}>
            {ghostText}
          </div>
        )}

        {isWelcome && !ghostText && !value && animatedPlaceholder && (
          <div className={`chat-animated-placeholder${attachments.length > 0 ? ' chat-animated-placeholder--below-attachments' : ''}`}>
            {animatedPlaceholder}
          </div>
        )}

        <textarea
          ref={textareaRef}
          value={value}
          onChange={e => {
            const v = e.target.value
            setValue(v)
            adjustHeight()
            if (v.startsWith('/') && v.length > 0) {
              setSlashMenuOpen(true)
              setSlashSelectedIndex(0)
            } else {
              setSlashMenuOpen(false)
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={isWelcome && animatedPlaceholderText ? '' : placeholder}
          disabled={disabled}
          rows={3}
          className={`chat-textarea${disabled ? ' chat-textarea--disabled' : ''}`}
          style={{ minHeight: MIN_TEXTAREA_HEIGHT }}
        />

        <div className="chat-toolbar">
          <div className="chat-toolbar-left">

            {/* Plus-knapp */}
            <div className="toolbar-popup-anchor" ref={plusMenuRef}>
              <button
                className={`toolbar-btn toolbar-btn--icon${showPlusMenu ? ' toolbar-btn--active' : ''}`}
                title={t.chat.addAttachment}
                onClick={() => setShowPlusMenu(v => !v)}
              >
                <Plus size={18} />
              </button>

              {showPlusMenu && (
                <div className="plus-menu">
                  <button
                    className="plus-menu-item"
                    onClick={() => {
                      setShowPlusMenu(false)
                      setShowSkillsDropdown(v => !v)
                    }}
                  >
                    <Zap size={14} className="plus-menu-icon" />
                    <span>Bruk Skills</span>
                    {enabledSkillsCount > 0 && (
                      <span className="plus-menu-badge">{enabledSkillsCount}</span>
                    )}
                    <ChevronRight size={12} className="plus-menu-chevron s-text-muted" />
                  </button>
                  <button
                    className="plus-menu-item"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Paperclip size={14} className="plus-menu-icon" />
                    <span>Last opp filer</span>
                  </button>
                </div>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileUpload}
              accept="*/*"
            />

            {/* Skills-knapp — hidden unless skills are active */}
            {enabledSkillsCount > 0 && (
              <div className="toolbar-popup-anchor" ref={skillsRef}>
                <button
                  className={`toolbar-btn skill-active${showSkillsDropdown ? ' toolbar-btn--skills-active' : ''}`}
                  title={t.settings.skills}
                  onClick={() => {
                    setShowSkillsDropdown(v => !v)
                    setShowConnectorsPopup(false)
                  }}
                >
                  <Zap size={18} />
                  <span className="toolbar-badge">{enabledSkillsCount}</span>
                </button>
                {showSkillsDropdown && (
                  <SkillsDropdown
                    skills={skills}
                    onToggle={handleSkillToggle}
                    onClose={() => setShowSkillsDropdown(false)}
                    onOpenSettings={() => {
                      setSettingsOpen(true)
                      window.dispatchEvent(new CustomEvent('sine:open-settings-tab', { detail: { tab: 'skills' } }))
                    }}
                  />
                )}
              </div>
            )}

            {/* Connectors-knapp - alltid tilgjengelig i samtalen */}
            <div className="toolbar-popup-anchor" ref={connectorsRef}>
              <button
                className={`toolbar-btn${connectedCount > 0 ? ' connector-active toolbar-btn--connector-on' : ''}${showConnectorsPopup ? ' toolbar-btn--active' : ''}`}
                title={t.settings.connections}
                onClick={() => {
                  setShowConnectorsPopup(v => !v)
                  setShowSkillsDropdown(false)
                }}
              >
                <Plug size={18} />
                {connectedCount > 0 && (
                  <span className="toolbar-badge connector-badge">{connectedCount}</span>
                )}
              </button>
              {showConnectorsPopup && (
                <ConnectorsPopup
                  statuses={connectorStatuses}
                  capabilities={connectorCapabilities}
                  disabled={settings.connectorDisabled ?? {}}
                  onClose={() => setShowConnectorsPopup(false)}
                  connectLabel={language === 'en' ? 'Connect' : 'Koble til'}
                  manageLabel={language === 'en' ? 'Manage connectors' : 'Administrer connectors'}
                  enableLabel={t.chat.enableConnector}
                  disableLabel={t.chat.disableConnector}
                  isWelcome={isWelcome}
                  enabledConnectors={enabledConnectors}
                  onToggleConnector={onToggleConnector}
                  onOpenSettings={() => {
                    setSettingsOpen(true)
                    window.dispatchEvent(new CustomEvent('sine:open-settings-tab', { detail: { tab: 'connectors' } }))
                  }}
                  onConnect={async (id) => {
                    const API_BASE = import.meta.env.VITE_API_URL ?? ''
                    if (id === 'github') {
                      try {
                        const { apiFetch } = await import('@/lib/api')
                        const res = await apiFetch(`${API_BASE}/api/github/auth-url`)
                        const data = await res.json()
                        if (data.auth_url) {
                          // Preserve active conversation so it can be restored after OAuth redirect
                          const pendingConvId = sessionStorage.getItem('sine_active_conversation_id')
                          if (pendingConvId) {
                            sessionStorage.setItem('sine_pending_conversation_id', pendingConvId)
                          }
                          window.location.href = data.auth_url
                        }
                      } catch (e) {
                        console.error('GitHub auth-url feil:', e)
                      }
                    } else {
                      setSettingsOpen(true)
                      window.dispatchEvent(new CustomEvent('sine:open-settings-tab', { detail: { tab: 'connectors' } }))
                    }
                  }}
                  onToggleDisabled={(id) => {
                    const prev = settings.connectorDisabled ?? {}
                    updateSettings({ connectorDisabled: { ...prev, [id]: !prev[id] } })
                  }}
                  openDownward={compact}
                />
              )}
            </div>

            <button
              className={`toolbar-btn toolbar-btn--icon${isTerminalOpen ? ' toolbar-btn--terminal-open' : agentState && ['planning', 'running'].includes(agentState.status) ? ' toolbar-btn--agent-running' : ''}`}
              title={onOpenTerminal ? t.chat.terminal : 'Sine Computer åpnes når en agentisk oppgave kjører i denne samtalen'}
              onClick={onOpenTerminal}
              disabled={!onOpenTerminal}
            >
              <Monitor size={18} />
            </button>


            {/* Wide Research-knapp fjernet */}

            {/* Bildegenerering håndteres automatisk via intent-deteksjon */}
            {/* Agent-innstillinger */}
            {showAgentControls && onAgentSettingsChange && (
              <AgentSettingsPopover
                settings={effectiveAgentSettings}
                onSettingsChange={(s) => {
                  onAgentSettingsChange(s)
                  onAgentModeChange?.(s.agentMode)
                }}
                memory={memory}
                onAddMemory={onAddMemory}
                onRemoveMemory={onRemoveMemory}
                onClearMemory={onClearMemory}
              />
            )}
          </div>

          <div className="chat-toolbar-right">
            <button
              className={`mic-btn${isRecording ? ' mic-btn--recording' : ''}`}
              title={isRecording ? 'Stopp opptak' : t.chat.voiceInput}
              aria-label={isRecording ? 'Stopp opptak' : t.chat.voiceInput}
              onClick={handleVoiceInput}
            >
              <Mic size={18} />
            </button>

            {isBusy ? (
              <button onClick={onStop} className="send-btn" title={t.chat.stop} aria-label={t.chat.stop}>
                <Square size={12} fill="#1C1C1C" className="send-btn-stop-icon" />
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={!canSend}
                className={`send-btn${canSend ? ' active' : ''}`}
                title={t.chat.send}
                aria-label={t.chat.send}
              >
                <ArrowUp size={16} strokeWidth={2.5} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Suggestion pills (below input, not in compact mode, only when no messages yet) ── */}
      {!compact && !hasMessages && (
        <SuggestionPills
          useAgentMode={showAgentControls}
          suggestions={suggestionOverride}
          animationSeed={welcomeAnimationKey}
          onSelect={(text) => {
            setValue(text)
            setGhostText('')
            setTimeout(() => {
              textareaRef.current?.focus()
              adjustHeight()
            }, 50)
          }}
          onHoverPreview={(text) => {
            if (!value) setGhostText(text)
          }}
          onHoverEnd={() => setGhostText('')}
        />
      )}

      {!compact && (
        <p className="chat-disclaimer">
          Sine kan gjøre feil. Sjekk viktig informasjon.
        </p>
      )}
    </div>
  )
}
